import Card from "components/Card/Card";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import LoadingSpinner from "views/Components/loadingSpinner";
import {
  downloadClaimRiskRetentionExport,
  getClaimRiskCampaignDetails,
  getClaimRiskCampaigns,
  getClaimRiskCampaignStatus,
  getClaimRiskRetentionFallbackClaims,
  getClaimRiskRetentionFallbackOrders,
  getClaimRiskRetentionProfile,
  getClaimRiskRetentionProfiles,
  getClaimRiskRetentionRefreshRun,
  previewClaimRiskCampaign,
  sendClaimRiskCampaign,
  startClaimRiskRetentionRefresh,
} from "../../../api/claimRiskRetention/claimRiskRetention";
import "./claimRiskRetention.css";

const showMeOptions = [
  { value: "", label: "All customers" },
  { value: "follow_up_today", label: "Need follow-up today" },
  { value: "one_order", label: "Only one pharmacy order" },
  { value: "two_orders", label: "Exactly two pharmacy orders" },
  { value: "repeat_customers", label: "Repeat pharmacy customers" },
  { value: "ordered_last_6_not_this_month", label: "Ordered recently, not this month" },
  { value: "no_order_this_month", label: "No order this month" },
  { value: "inactive_60_days", label: "Inactive for 60+ days" },
  { value: "high_claim_frequency", label: "High claim frequency" },
  { value: "high_frequency_chronic", label: "High-frequency chronic claimants" },
  { value: "chronic_detected", label: "Chronic disease detected" },
  { value: "chronic_care", label: "Chronic care" },
];

const riskOptions = [
  { value: "", label: "Any risk" },
  { value: "High", label: "High risk" },
  { value: "Medium", label: "Medium risk" },
  { value: "Low", label: "Low risk" },
];

const sortOptions = [
  { value: "risk_score", label: "Risk score" },
  { value: "last_order", label: "Last order" },
  { value: "orders", label: "Total orders" },
  { value: "orders_last_6_months", label: "Orders last 6 months" },
  { value: "claims", label: "Claims" },
  { value: "claim_value", label: "Claim value" },
  { value: "approved_claim_value", label: "Approved claim value" },
  { value: "pharmacy_revenue", label: "Pharmacy revenue" },
  { value: "missed_refills", label: "Missed refills" },
  { value: "average_order_value", label: "Average order value" },
  { value: "last_claim", label: "Last claim" },
];

const sortDirectionOptions = [
  { value: "desc", label: "High to low" },
  { value: "asc", label: "Low to high" },
];

const pharmacyRevenueOptions = [
  { value: "", label: "Any pharmacy revenue" },
  { value: "zero", label: "No pharmacy revenue" },
  { value: "positive", label: "Has pharmacy revenue" },
];

const defaultDiseaseOptions = [
  "Diabetes",
  "Blood Pressure",
  "Hypertension",
  "Cholesterol",
  "Heart / Cardiac",
  "Thyroid",
  "Asthma / Respiratory",
  "Kidney",
  "Liver",
  "Unknown",
];

const normalizeDiseaseText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const canonicalDiseaseName = (value) => {
  const name = String(value || "").trim();
  const normalized = normalizeDiseaseText(name);

  if (!normalized || normalized.indexOf("diaper") !== -1) {
    return "";
  }

  if (
    ["diabetic", "diabetics", "diabetes mellitus", "diabetes"].indexOf(normalized) !== -1
  ) {
    return "Diabetes";
  }

  return name;
};

const cleanDiseaseOptions = (options = []) => {
  const normalizedOptions = {};

  [...defaultDiseaseOptions, ...options].forEach((option) => {
    const canonical = canonicalDiseaseName(option);
    if (canonical) {
      normalizedOptions[canonical.toLowerCase()] = canonical;
    }
  });

  return Object.values(normalizedOptions).sort((a, b) => a.localeCompare(b));
};

const filterDiseaseOptions = (options, filterValue) => {
  const needle = normalizeDiseaseText(filterValue);
  if (!needle) {
    return options;
  }

  return options.filter((option) =>
    normalizeDiseaseText(option.label)
      .split(" ")
      .some((word) => word.indexOf(needle) === 0),
  );
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-LK", {
    maximumFractionDigits: 0,
  });

const formatNumber = (value) => Number(value || 0).toLocaleString("en-LK");
const TABLE_PAGE_SIZE = 50;
const eligibleOrderStatuses = [4, 8];
const eligibleClaimStatuses = [2, 4];
const parseLeadIds = (value) =>
  String(value || "")
    .split(/[\s,]+/)
    .map((item) => Number(item.trim()))
    .filter((item) => item);

const compactObject = (value) =>
  Object.keys(value).reduce((acc, key) => {
    if (value[key] !== "" && value[key] !== null && value[key] !== undefined) {
      acc[key] = value[key];
    }
    return acc;
  }, {});

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
};

const smsParts = (message) => {
  const length = (message || "").length;
  return length <= 160 ? 1 : Math.ceil(length / 153);
};

const selectedValue = (options, value) =>
  options.find((option) => option.value === value) || options[0];

const valueOrFallback = (value, fallback) =>
  value === undefined || value === null ? fallback : value;

const unwrapRows = (payload) => {
  const data = payload && payload.data !== undefined ? payload.data : payload;

  if (Array.isArray(data)) {
    return data;
  }

  if (data && Array.isArray(data.data)) {
    return data.data;
  }

  if (data && data.data && Array.isArray(data.data.data)) {
    return data.data.data;
  }

  return [];
};

const numericValue = (value) => Number(value || 0);

const orderStatusLabel = (status) => {
  const labels = {
    4: "Completed",
    8: "Pending Payment",
  };

  return labels[Number(status)] || "Unknown";
};

const claimStatusLabel = (status) => {
  const labels = {
    2: "Processed",
    4: "Completed",
  };

  return labels[Number(status)] || "Unknown";
};

const claimTypeLabel = (type) => {
  const labels = {
    1: "Medicine",
    2: "Lab",
    3: "Consultation",
    4: "Other",
  };

  return labels[Number(type)] || "Unknown";
};

const normalizeOrderItems = (order) => {
  const items =
    order.items ||
    order.orderItem ||
    order.orderitem ||
    order.order_items ||
    order.orderitemdata ||
    [];

  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => ({
    id: item.id,
    name:
      item.name ||
      item.description ||
      item.item_ordered ||
      (item.medication && item.medication.title) ||
      (item.medication_package && item.medication_package.package_name) ||
      "Item",
    quantity: numericValue(item.quantity),
    subtotal: numericValue(item.subtotal || item.total || item.value),
    refill_requested: !!item.refill_requested,
  }));
};

const normalizeOrderRow = (order) => ({
  id: order.id,
  order_date: order.order_date || order.created_at || order.order_display_date,
  status: order.status || orderStatusLabel(order.order_status || order.status_code),
  status_code: Number(order.order_status || order.status_code || 0),
  district:
    order.district ||
    (order.address && order.address.address_city && order.address.address_city.city_name) ||
    (order.address && order.address.city_name) ||
    "",
  total_cost: numericValue(order.total_cost || order.basket_value),
  items: normalizeOrderItems(order),
});

const normalizeClaimRow = (claim) => ({
  id: claim.id,
  created_at: claim.created_at || claim.claim_date,
  source: claim.source || (claim.order_id ? "system" : "manual"),
  claim_type: claim.claim_type_label || claimTypeLabel(claim.claim_type || claim.type),
  status: claim.status_label || claimStatusLabel(claim.status || claim.status_code),
  status_code: Number(claim.status || claim.status_code || 0),
  claimed_value: numericValue(claim.claimed_value || claim.value),
  extracted_items: claim.extracted_items || [],
  prescription_items: claim.prescription_items || [],
});

const buildMedicineUsage = (orders = []) => {
  const medicines = {};

  orders.forEach((order) => {
    (order.items || []).forEach((item) => {
      const name = item.name || "Item";
      if (!medicines[name]) {
        medicines[name] = {
          name,
          orders: 0,
          value: 0,
        };
      }
      medicines[name].orders += 1;
      medicines[name].value += numericValue(item.subtotal);
    });
  });

  return Object.values(medicines).sort((a, b) => b.value - a.value);
};

const mergeFallbackDetail = (detail, fallbackOrders, fallbackClaims) => {
  const orders = fallbackOrders && fallbackOrders.length ? fallbackOrders : detail.orders || [];
  const claims = fallbackClaims && fallbackClaims.length ? fallbackClaims : detail.claims || [];
  const systemClaims = claims.filter((claim) => claim.source === "system");
  const manualClaims = claims.filter((claim) => claim.source !== "system");
  const claimValue = claims.reduce((sum, claim) => sum + numericValue(claim.claimed_value), 0);
  const orderValue = orders.reduce((sum, order) => sum + numericValue(order.total_cost), 0);

  return {
    ...detail,
    orders,
    claims,
    medicine_usage:
      detail.medicine_usage && detail.medicine_usage.length
        ? detail.medicine_usage
        : buildMedicineUsage(orders),
    recovery_summary: {
      ...(detail.recovery_summary || {}),
      total_claims: claims.length || (detail.recovery_summary || {}).total_claims,
      approved_claims: claims.length || (detail.recovery_summary || {}).approved_claims,
      total_claim_value: claimValue || (detail.recovery_summary || {}).total_claim_value,
      approved_claim_value: claimValue || (detail.recovery_summary || {}).approved_claim_value,
      pharmacy_order_count: orders.length || (detail.recovery_summary || {}).pharmacy_order_count,
      pharmacy_order_value: orderValue || (detail.recovery_summary || {}).pharmacy_order_value,
      system_claims_sampled: systemClaims.length,
      manual_claims_sampled: manualClaims.length,
      system_claim_value_sampled: systemClaims.reduce(
        (sum, claim) => sum + numericValue(claim.claimed_value),
        0,
      ),
      manual_claim_value_sampled: manualClaims.reduce(
        (sum, claim) => sum + numericValue(claim.claimed_value),
        0,
      ),
    },
  };
};

const retentionSelectStyles = {
  menu: (provided) => ({
    ...provided,
    zIndex: 1000,
  }),
};

const emptyFilters = {
  search: "",
  organization_id: "",
  district: "",
  condition_name: "",
  risk_level: "",
  show_me: "",
  sort_by: "risk_score",
  sort_direction: "desc",
  min_orders: "",
  min_claims: "",
  min_claim_value: "",
  min_pharmacy_revenue: "",
  pharmacy_revenue_status: "",
};

const ClaimRiskRetention = () => {
  const token = JSON.parse(localStorage.getItem("user"));
  const authToken = token && token.token;
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [summary, setSummary] = useState({});
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [run, setRun] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [diseaseOptions, setDiseaseOptions] = useState(cleanDiseaseOptions(defaultDiseaseOptions));
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState(["Colombo"]);
  const [showAdvancedSync, setShowAdvancedSync] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignSending, setCampaignSending] = useState(false);
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [campaignMessage, setCampaignMessage] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [campaignHistory, setCampaignHistory] = useState([]);
  const [campaignDetailLoading, setCampaignDetailLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignRecipients, setCampaignRecipients] = useState([]);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileDetailLoading, setProfileDetailLoading] = useState(false);
  const [selectedProfileDetail, setSelectedProfileDetail] = useState(null);
  const [profileDetailError, setProfileDetailError] = useState("");
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [syncForm, setSyncForm] = useState({
    chunk_size: 10,
    max_claims: 10,
    max_gemini_calls: 16,
    force: false,
    lead_ids: "",
    mobile_numbers: "",
  });

  useEffect(() => {
    loadData(1);
  }, []);

  useEffect(() => {
    if (!run || !["pending", "running"].includes(run.status)) {
      return undefined;
    }

    const timer = setTimeout(() => {
      getClaimRiskRetentionRefreshRun(authToken, run.id).then((res) => {
        if (res && res.success) {
          setRun(res.data);
          if (["completed", "stopped", "failed"].includes(res.data.status)) {
            loadData(1);
          }
        }
      });
    }, 3500);

    return () => clearTimeout(timer);
  }, [run, authToken]);

  useEffect(() => {
    if (!activeCampaign || !["queued", "running"].includes(activeCampaign.status)) {
      return undefined;
    }

    const timer = setTimeout(() => {
      getClaimRiskCampaignStatus(authToken, activeCampaign.id).then((res) => {
        if (res && res.success) {
          setActiveCampaign(res.data);
        }
      });
    }, 3500);

    return () => clearTimeout(timer);
  }, [activeCampaign, authToken]);

  const requestParams = (page = 1, activeFilters = filters) => ({
    ...activeFilters,
    page,
    per_page: TABLE_PAGE_SIZE,
  });

  const loadData = (page = 1, activeFilters = filters) => {
    setLoading(true);
    const params = requestParams(page, activeFilters);

    getClaimRiskRetentionProfiles(authToken, params)
      .then((profileRes) => {
        if (profileRes && profileRes.success) {
          const filterOptions = profileRes.data.filters || {};
          const conditions = cleanDiseaseOptions(filterOptions.condition_options || defaultDiseaseOptions);
          const organizations = filterOptions.organization_options || [];
          const districts = filterOptions.district_options || ["Colombo"];

          setSummary(profileRes.data.summary || {});
          setProfiles((profileRes.data.profiles && profileRes.data.profiles.data) || []);
          setPagination(
            profileRes.data.profiles || { current_page: 1, last_page: 1 },
          );
          setDiseaseOptions(conditions);
          setOrganizationOptions(organizations);
          setDistrictOptions(districts);
        }
      })
      .finally(() => setLoading(false));
  };

  const updateFilter = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const updateFilterAndLoad = (key, value) => {
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    loadData(1, nextFilters);
  };

  const applyFilters = () => {
    loadData(1);
  };

  const applyQuickFilter = (quickFilters) => {
    const nextFilters =
      typeof quickFilters === "string"
        ? { ...filters, show_me: quickFilters }
        : { ...filters, ...quickFilters };
    setFilters(nextFilters);
    loadData(1, nextFilters);
  };

  const applySort = (sortBy) => {
    const isSameSort = filters.sort_by === sortBy;
    const nextDirection = isSameSort && filters.sort_direction === "desc" ? "asc" : "desc";
    const nextFilters = {
      ...filters,
      sort_by: sortBy,
      sort_direction: nextDirection,
    };

    setFilters(nextFilters);
    loadData(1, nextFilters);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    setExporting(true);
    downloadClaimRiskRetentionExport(authToken, requestParams(1))
      .then((response) => {
        downloadBlob(response.data, `claim-risk-retention-${Date.now()}.xlsx`);
      })
      .catch(() => {
        Swal.fire("Export failed", "Please try again.", "error");
      })
      .finally(() => setExporting(false));
  };

  const campaignPayload = (overrides = {}) => ({
    ...filters,
    ...overrides,
  });

  const previewCampaign = (messageOverride) => {
    const hasMessageOverride = messageOverride !== undefined && messageOverride !== null;

    setCampaignLoading(true);
    previewClaimRiskCampaign(
      authToken,
      campaignPayload({
        message_body: hasMessageOverride ? messageOverride : campaignMessage || undefined,
      }),
    )
      .then((res) => {
        if (res && res.success) {
          setCampaignPreview(res.data);
          setCampaignMessage(res.data.message_body || "");
          if (!campaignName) {
            setCampaignName(`Claim Risk SMS - ${new Date().toLocaleDateString()}`);
          }
        } else {
          Swal.fire("Preview failed", "No matching SMS audience found.", "warning");
        }
      })
      .catch(() => {
        Swal.fire("Preview failed", "Please try again.", "error");
      })
      .finally(() => setCampaignLoading(false));
  };

  const openCampaignModal = () => {
    setCampaignModalOpen(true);
    setCampaignPreview(null);
    setCampaignMessage("");
    setActiveCampaign(null);
    previewCampaign(null);
  };

  const sendCampaign = () => {
    const count = campaignPreview ? campaignPreview.recipient_count : 0;

    if (!campaignMessage || campaignMessage.length < 20) {
      Swal.fire("Message is too short", "Please add a proper SMS message before sending.", "warning");
      return;
    }

    if (!count) {
      Swal.fire("No recipients", "This filter has no customers with mobile numbers.", "warning");
      return;
    }

    Swal.fire({
      title: "Send SMS campaign?",
      html: `<font size="4px">This will send a real SMS to <b>${formatNumber(
        count,
      )}</b> customers matching the current filters.</font>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, send campaign",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      setCampaignSending(true);
      sendClaimRiskCampaign(
        authToken,
        campaignPayload({
          campaign_name: campaignName,
          message_body: campaignMessage,
          confirm_send: true,
          max_recipients: 2000,
        }),
      )
        .then((res) => {
          if (res && res.success) {
            setActiveCampaign({
              id: res.data.campaign_id,
              status: res.data.status,
              recipient_count: res.data.recipient_count,
              sent_count: 0,
              failed_count: 0,
            });
            Swal.fire("Campaign queued", "SMS sending has started in the background.", "success");
          } else {
            Swal.fire("Campaign not sent", res.message || "Please check the audience and try again.", "warning");
          }
        })
        .catch(() => {
          Swal.fire("Campaign not sent", "Please try again.", "error");
        })
        .finally(() => setCampaignSending(false));
    });
  };

  const loadCampaignHistory = () => {
    setHistoryLoading(true);
    getClaimRiskCampaigns(authToken)
      .then((res) => {
        if (res && res.success) {
          setCampaignHistory((res.data.campaigns && res.data.campaigns.data) || []);
        }
      })
      .catch(() => {
        Swal.fire("History failed", "Please try again.", "error");
      })
      .finally(() => setHistoryLoading(false));
  };

  const openHistoryModal = () => {
    setHistoryModalOpen(true);
    setSelectedCampaign(null);
    setCampaignRecipients([]);
    loadCampaignHistory();
  };

  const openCampaignDetails = (campaignId) => {
    setCampaignDetailLoading(true);
    getClaimRiskCampaignDetails(authToken, campaignId)
      .then((res) => {
        if (res && res.success) {
          setSelectedCampaign(res.data.campaign);
          setCampaignRecipients(res.data.recipients || []);
        }
      })
      .catch(() => {
        Swal.fire("Campaign details failed", "Please try again.", "error");
      })
      .finally(() => setCampaignDetailLoading(false));
  };

  const profileDetailFromRow = (profile) => ({
    customer: {
      id: profile.lead_id || (profile.lead && profile.lead.id) || "",
      name: profile.lead ? profile.lead.name : "",
      mobile_no: profile.lead ? profile.lead.mobile_no : "",
      company_name: profile.lead && profile.lead.b2b ? profile.lead.b2b.company_name : "",
      district: profile.delivery_region || "",
    },
    profile: {
      lead_id: profile.lead_id || (profile.lead && profile.lead.id) || "",
      risk_level: profile.risk_level,
      business_status: profile.business_status,
      customer_segment: profile.customer_segment,
      primary_condition: renderCondition(profile),
      follow_up_reason: profile.follow_up_reason,
      recommended_next_step: profile.recommended_next_step,
    },
    recovery_summary: {
      total_claims: profile.total_claims,
      total_claim_value: profile.total_claim_value,
      approved_claim_value: profile.approved_claim_value,
      pharmacy_order_count: profile.pharmacy_order_count,
      pharmacy_order_value: profile.pharmacy_order_value,
      missed_refill_count: profile.missed_refill_count,
    },
    medicine_usage: [],
    orders: [],
    claims: [],
    csr_prompt: profile.recommended_next_step,
  });

  const normalizeProfileDetail = (data, rowDetail) => {
    if (!data) {
      return rowDetail;
    }

    if (data.customer || data.profile || data.recovery_summary) {
      return {
        ...rowDetail,
        ...data,
        customer: { ...rowDetail.customer, ...(data.customer || {}) },
        profile: { ...rowDetail.profile, ...(data.profile || {}) },
        recovery_summary: {
          ...rowDetail.recovery_summary,
          ...(data.recovery_summary || {}),
        },
        medicine_usage: data.medicine_usage || rowDetail.medicine_usage,
        orders: data.orders || rowDetail.orders,
        claims: data.claims || rowDetail.claims,
        csr_prompt: data.csr_prompt || rowDetail.csr_prompt,
      };
    }

    return {
      ...rowDetail,
      customer: {
        ...rowDetail.customer,
        id: data.lead_id || rowDetail.customer.id,
        name: data.lead && data.lead.name ? data.lead.name : rowDetail.customer.name,
        mobile_no: data.lead && data.lead.mobile_no ? data.lead.mobile_no : rowDetail.customer.mobile_no,
        company_name:
          data.lead && data.lead.b2b && data.lead.b2b.company_name
            ? data.lead.b2b.company_name
            : rowDetail.customer.company_name,
        district: data.delivery_region || rowDetail.customer.district,
      },
      profile: {
        ...rowDetail.profile,
        lead_id: data.lead_id || rowDetail.profile.lead_id,
        risk_level: data.risk_level || rowDetail.profile.risk_level,
        primary_condition: renderCondition(data) || rowDetail.profile.primary_condition,
      },
      recovery_summary: {
        ...rowDetail.recovery_summary,
        total_claims: valueOrFallback(data.total_claims, rowDetail.recovery_summary.total_claims),
        total_claim_value: valueOrFallback(data.total_claim_value, rowDetail.recovery_summary.total_claim_value),
        approved_claim_value: valueOrFallback(data.approved_claim_value, rowDetail.recovery_summary.approved_claim_value),
        pharmacy_order_count: valueOrFallback(data.pharmacy_order_count, rowDetail.recovery_summary.pharmacy_order_count),
        pharmacy_order_value: valueOrFallback(data.pharmacy_order_value, rowDetail.recovery_summary.pharmacy_order_value),
        missed_refill_count: valueOrFallback(data.missed_refill_count, rowDetail.recovery_summary.missed_refill_count),
      },
    };
  };

  const loadFallbackDetailRows = (leadId, currentDetail) =>
    Promise.all([
      getClaimRiskRetentionFallbackOrders(authToken, leadId).catch(() => null),
      getClaimRiskRetentionFallbackClaims(authToken, leadId).catch(() => null),
    ]).then(([orderRes, claimRes]) => {
      const fallbackOrders = unwrapRows(orderRes)
        .map(normalizeOrderRow)
        .filter((order) => eligibleOrderStatuses.indexOf(order.status_code) !== -1);
      const fallbackClaims = unwrapRows(claimRes)
        .map(normalizeClaimRow)
        .filter((claim) => eligibleClaimStatuses.indexOf(claim.status_code) !== -1);

      if (!fallbackOrders.length && !fallbackClaims.length) {
        return currentDetail;
      }

      return mergeFallbackDetail(currentDetail, fallbackOrders, fallbackClaims);
    });

  const openProfileDetails = (profile) => {
    const leadId = profile && (profile.lead_id || (profile.lead && profile.lead.id) || profile.id);

    if (!leadId) {
      Swal.fire("Customer details unavailable", "This row does not have a customer ID to open.", "warning");
      return;
    }

    const rowDetail = profileDetailFromRow(profile);

    setProfileModalOpen(true);
    setSelectedProfileDetail(rowDetail);
    setProfileDetailError("");
    setProfileDetailLoading(true);

    getClaimRiskRetentionProfile(authToken, leadId)
      .then((res) => {
        let detail = rowDetail;
        if (res && res.success && res.data) {
          detail = normalizeProfileDetail(res.data, rowDetail);
          setSelectedProfileDetail(detail);
        } else {
          setProfileDetailError("Full order and claim details were not returned for this customer yet.");
        }

        return loadFallbackDetailRows(leadId, detail).then((mergedDetail) => {
          setSelectedProfileDetail(mergedDetail);
          if ((mergedDetail.orders || []).length || (mergedDetail.claims || []).length) {
            setProfileDetailError("");
          }
        });
      })
      .catch(() => {
        setProfileDetailError("Full order and claim details could not be loaded. Showing the table summary for now.");
        return loadFallbackDetailRows(leadId, rowDetail).then((mergedDetail) => {
          setSelectedProfileDetail(mergedDetail);
          if ((mergedDetail.orders || []).length || (mergedDetail.claims || []).length) {
            setProfileDetailError("");
          }
        });
      })
      .finally(() => setProfileDetailLoading(false));
  };

  const updateSync = (key, value) => {
    setSyncForm({ ...syncForm, [key]: value });
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    loadData(1, emptyFilters);
  };

  const startSync = () => {
    const leadIds = parseLeadIds(syncForm.lead_ids);
    const mobileNumbers = String(syncForm.mobile_numbers || "").trim();
    const isTargetedSync = leadIds.length > 0 || mobileNumbers.length > 0;

    Swal.fire({
      title: isTargetedSync ? "Start customer sync?" : "Start safe sync?",
      html:
        "<font size='4px'>The Gemini limit will stop the run automatically before it can exceed the allowed calls.</font>",
      showCancelButton: true,
      confirmButtonText: "Start",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      setSyncLoading(true);
      startClaimRiskRetentionRefresh(authToken, compactObject({
        chunk_size: Number(syncForm.chunk_size || 50),
        max_claims: Number(syncForm.max_claims || 500),
        max_gemini_calls: Number(syncForm.max_gemini_calls || 0),
        organization_id: filters.organization_id || null,
        force: !!syncForm.force,
        process_inline: true,
        lead_ids: leadIds.length ? leadIds : undefined,
        mobile_numbers: mobileNumbers || undefined,
      }))
        .then((res) => {
          if (res && res.success) {
            setRun(res.data);
            if (["completed", "stopped", "failed"].includes(res.data.status)) {
              loadData(1);
            }
          } else {
            Swal.fire(
              "Sync failed",
              (res && res.message) || "The manual sync could not be started.",
              "error",
            );
          }
        })
        .catch((error) => {
          Swal.fire(
            "Sync failed",
            error && error.message ? error.message : "The manual sync request did not complete.",
            "error",
          );
        })
        .finally(() => setSyncLoading(false));
    });
  };

  const diseaseSelectOptions = [
    { value: "", label: "All diseases" },
    ...cleanDiseaseOptions(diseaseOptions).map((name) => ({ value: name, label: name })),
  ];

  const organizationSelectOptions = [
    { value: "", label: "All companies" },
    ...organizationOptions.map((organization) => ({
      value: String(organization.id),
      label: organization.name,
    })),
  ];

  const districtSelectOptions = [
    { value: "", label: "All districts" },
    ...districtOptions.map((district) => ({
      value: district,
      label: district,
    })),
  ];

  const sortHeader = (label, sortBy) => {
    const active = filters.sort_by === sortBy;
    const indicator = active ? (filters.sort_direction === "asc" ? "↑" : "↓") : "↕";

    return (
      <button
        className={`sort-header ${active ? "is-active" : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          applySort(sortBy);
        }}
        type="button"
      >
        <span className="sort-header-label">{label}</span>
        <span className="sort-header-icon">{indicator}</span>
      </button>
    );
  };

  const renderProfileDetailModal = () => {
    const detail = selectedProfileDetail || {};
    const customer = detail.customer || {};
    const profile = detail.profile || {};
    const recovery = detail.recovery_summary || {};
    const medicineUsage = detail.medicine_usage || [];
    const orders = detail.orders || [];
    const claims = detail.claims || [];

    return (
      <Modal
        className="claim-risk-profile-modal"
        show={profileModalOpen}
        onHide={() => setProfileModalOpen(false)}
        bsSize="large"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {customer.name || "Customer details"}
            {customer.mobile_no ? ` - ${customer.mobile_no}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {profileDetailLoading ? (
            <LoadingSpinner />
          ) : (
            <React.Fragment>
              {!selectedProfileDetail ? (
                <div className="profile-detail-empty">
                  Customer detail data is not available for this row yet.
                </div>
              ) : null}
              {profileDetailError ? (
                <div className="profile-detail-empty">
                  {profileDetailError}
                </div>
              ) : null}
              <div className="profile-detail-grid">
                <div>
                  <span>Company</span>
                  <b>{customer.company_name || "-"}</b>
                </div>
                <div>
                  <span>District</span>
                  <b>{customer.district || "-"}</b>
                </div>
                <div>
                  <span>Status</span>
                  <b>{profile.business_status || "-"}</b>
                </div>
                <div>
                  <span>Disease</span>
                  <b>{profile.primary_condition || "-"}</b>
                </div>
                <div>
                  <span>Claims</span>
                  <b>{formatNumber(recovery.total_claims)}</b>
                </div>
                <div>
                  <span>Claim value</span>
                  <b>Rs. {formatMoney(recovery.total_claim_value)}</b>
                </div>
                <div>
                  <span>Pharmacy revenue</span>
                  <b>Rs. {formatMoney(recovery.pharmacy_order_value)}</b>
                </div>
                <div>
                  <span>Missed refills</span>
                  <b>{formatNumber(recovery.missed_refill_count)}</b>
                </div>
              </div>

              <div className="csr-prompt">
                <b>CSR next step</b>
                <p>{detail.csr_prompt || profile.recommended_next_step || "-"}</p>
              </div>

              <Row>
                <Col md={6} xs={12}>
                  <h5>Medicine usage</h5>
                  <Table condensed responsive>
                    <thead>
                      <tr>
                        <th>Medicine</th>
                        <th>Orders</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {medicineUsage.length ? (
                        medicineUsage.slice(0, 8).map((item) => (
                          <tr key={item.name}>
                            <td>{item.name}</td>
                            <td>{formatNumber(item.orders)}</td>
                            <td>Rs. {formatMoney(item.value)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3">No pharmacy medicine history found</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
                <Col md={6} xs={12}>
                  <h5>Claim split</h5>
                  <Table condensed responsive>
                    <tbody>
                      <tr>
                        <td>System claims sampled</td>
                        <td>{formatNumber(recovery.system_claims_sampled)}</td>
                        <td>Rs. {formatMoney(recovery.system_claim_value_sampled)}</td>
                      </tr>
                      <tr>
                        <td>Manual claims sampled</td>
                        <td>{formatNumber(recovery.manual_claims_sampled)}</td>
                        <td>Rs. {formatMoney(recovery.manual_claim_value_sampled)}</td>
                      </tr>
                      <tr>
                        <td>Approved claim value</td>
                        <td colSpan="2">Rs. {formatMoney(recovery.approved_claim_value)}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>

              <h5>Recent orders</h5>
              <Table condensed responsive>
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>District</th>
                    <th>Items</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length ? (
                    orders.slice(0, 6).map((order) => (
                      <tr key={order.id}>
                        <td>#{order.id}</td>
                        <td>{formatDate(order.order_date)}</td>
                        <td>{order.status}</td>
                        <td>{order.district || "-"}</td>
                        <td>
                          {(order.items || [])
                            .slice(0, 3)
                            .map((item) => item.name)
                            .join(", ") || "-"}
                        </td>
                        <td>Rs. {formatMoney(order.total_cost)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">No pharmacy orders found</td>
                    </tr>
                  )}
                </tbody>
              </Table>

              <h5>Recent claims</h5>
              <Table condensed responsive>
                <thead>
                  <tr>
                    <th>Claim</th>
                    <th>Date</th>
                    <th>Source</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Value</th>
                    <th>Extracted items</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.length ? (
                    claims.slice(0, 8).map((claim) => (
                      <tr key={claim.id}>
                        <td>#{claim.id}</td>
                        <td>{formatDate(claim.created_at)}</td>
                        <td>{claim.source}</td>
                        <td>{claim.claim_type}</td>
                        <td>{claim.status}</td>
                        <td>Rs. {formatMoney(claim.claimed_value)}</td>
                        <td>
                          {[
                            ...(claim.prescription_items || []),
                            ...(claim.extracted_items || []),
                          ]
                            .slice(0, 3)
                            .map((item) => item.name || item.item || item.medicine || item.description)
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7">No recent claims found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </React.Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setProfileModalOpen(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  };

  const statusBadge = (status) => {
    const normalized = (status || "Active").toLowerCase().replace(/\s+/g, "-");
    return <span className={`status-badge status-${normalized}`}>{status || "Active"}</span>;
  };

  const renderCondition = (profile) => {
    if (profile.primary_condition) {
      return profile.primary_condition;
    }

    if (profile.conditions && profile.conditions.length && profile.conditions[0].name) {
      return profile.conditions[0].name;
    }

    return "Unknown";
  };

  const renderLastOrder = (profile) => {
    if (!profile.last_order_date) {
      return "No order yet";
    }

    if (profile.days_since_last_order === null || profile.days_since_last_order === undefined) {
      return profile.last_order_date;
    }

    return `${formatDate(profile.last_order_date)} (${profile.days_since_last_order} days)`;
  };

  const pageStart = pagination.total
    ? ((pagination.current_page || 1) - 1) * (pagination.per_page || TABLE_PAGE_SIZE) + 1
    : 0;
  const pageEnd = pagination.total
    ? Math.min(
        (pagination.current_page || 1) * (pagination.per_page || TABLE_PAGE_SIZE),
        pagination.total,
      )
    : 0;
  const hasMultiplePages = Number(pagination.last_page || 1) > 1;
  const currentPage = Number(pagination.current_page || 1);
  const lastPage = Number(pagination.last_page || 1);

  const renderPager = (className = "") =>
    hasMultiplePages ? (
      <div className={`retention-pagination ${className}`}>
        <Button
          bsSize="small"
          disabled={loading || currentPage <= 1}
          onClick={() => loadData(1)}
        >
          First
        </Button>
        <Button
          bsSize="small"
          disabled={loading || currentPage <= 1}
          onClick={() => loadData(currentPage - 1)}
        >
          Previous
        </Button>
        <span>
          Page {formatNumber(currentPage)} of {formatNumber(lastPage)}
        </span>
        <Button
          bsSize="small"
          bsStyle="primary"
          disabled={loading || currentPage >= lastPage}
          onClick={() => loadData(currentPage + 1)}
        >
          Next
        </Button>
        <Button
          bsSize="small"
          disabled={loading || currentPage >= lastPage}
          onClick={() => loadData(lastPage)}
        >
          Last
        </Button>
      </div>
    ) : null;

  const actionCards = [
    {
      key: "total_cashless_opd_customers",
      label: "Total cashless OPD customers",
      value: summary.total_cashless_opd_customers,
      detail: "All eligible OPD customers",
      filter: { show_me: "", risk_level: "" },
      tone: "care",
    },
    {
      key: "claim_submitted_customers",
      label: "Claim submitted customers",
      value: summary.claim_submitted_customers,
      detail: "Customers who placed claims",
      filter: { show_me: "", risk_level: "" },
      tone: "urgent",
    },
    {
      key: "high_risk",
      label: "High risk customers",
      value: summary.high_risk,
      detail: "Needs priority review",
      filter: { risk_level: "High", show_me: "" },
      tone: "warning",
    },
    {
      key: "high_frequency_chronic_claimants",
      label: "High-frequency chronic claimants",
      value: summary.high_frequency_chronic_claimants,
      detail: "5+ claims with disease signal",
      filter: { show_me: "high_frequency_chronic", risk_level: "" },
      tone: "neutral",
    },
    {
      key: "chronic_care_customers",
      label: "Chronic care",
      value: summary.chronic_care_customers,
      detail: "Repeat customers with chronic signal",
      filter: { show_me: "chronic_care", risk_level: "" },
      tone: "focus",
    },
    {
      key: "follow_up_today",
      label: "Need follow-up",
      value: summary.follow_up_today,
      detail: "Call or message first",
      filter: { show_me: "follow_up_today", risk_level: "" },
      tone: "money",
    },
  ];

  return (
    <div className="main-content claim-risk-retention">
      <Grid fluid>
        <Row>
          <Col md={12}>
            <Card
              content={
                <React.Fragment>
                  <div className="engine-header">
                    <div>
                      <h4>Claim Risk & Retention Engine</h4>
                      <p>Who needs follow-up, why they are at risk, and what action to take</p>
                    </div>
                    <div className="engine-actions">
                      <span className="sync-count-pill">
                        {formatNumber(summary.unsynced_claims)} pending sync
                      </span>
                      <Button
                        bsStyle="primary"
                        disabled={loading || Number(summary.total_profiles || 0) === 0}
                        onClick={openCampaignModal}
                      >
                        Run SMS Campaign
                      </Button>
                      <Button onClick={openHistoryModal}>Campaign History</Button>
                      <Button onClick={() => setShowAdvancedSync(!showAdvancedSync)}>
                        {showAdvancedSync ? "Hide Advanced Sync" : "Advanced Sync"}
                      </Button>
                    </div>
                  </div>

                  <div className="action-grid">
                    {actionCards.map((card) => (
                      <button
                        className={`action-card action-${card.tone} ${
                          Object.keys(card.filter).every((key) => filters[key] === card.filter[key])
                            ? "is-active"
                            : ""
                        }`}
                        key={card.key}
                        onClick={() => applyQuickFilter(card.filter)}
                        type="button"
                      >
                        <span className="action-label">{card.label}</span>
                        <span className="action-value">
                          {typeof card.value === "string"
                            ? card.value
                            : formatNumber(card.value)}
                        </span>
                        <span className="action-detail">{card.detail}</span>
                      </button>
                    ))}
                  </div>

                  <Row className="simple-filter-bar">
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Show me</ControlLabel>
                      <Select
                        clearable={false}
                        styles={retentionSelectStyles}
                        value={selectedValue(showMeOptions, filters.show_me)}
                        onChange={(item) => updateFilterAndLoad("show_me", item.value)}
                        options={showMeOptions}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>District</ControlLabel>
                      <Select
                        clearable={false}
                        styles={retentionSelectStyles}
                        value={selectedValue(districtSelectOptions, filters.district)}
                        onChange={(item) => updateFilterAndLoad("district", item.value)}
                        options={districtSelectOptions}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Company</ControlLabel>
                      <Select
                        clearable={false}
                        styles={retentionSelectStyles}
                        value={selectedValue(organizationSelectOptions, filters.organization_id)}
                        onChange={(item) => updateFilterAndLoad("organization_id", item.value)}
                        options={organizationSelectOptions}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Disease</ControlLabel>
                      <Select
                        clearable={false}
                        styles={retentionSelectStyles}
                        value={selectedValue(diseaseSelectOptions, filters.condition_name)}
                        onChange={(item) => updateFilterAndLoad("condition_name", item.value)}
                        options={diseaseSelectOptions}
                        filterOptions={filterDiseaseOptions}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <ControlLabel>Risk</ControlLabel>
                      <Select
                        clearable={false}
                        styles={retentionSelectStyles}
                        value={selectedValue(riskOptions, filters.risk_level)}
                        onChange={(item) => updateFilterAndLoad("risk_level", item.value)}
                        options={riskOptions}
                      />
                    </Col>
                    <Col md={2} sm={6} xs={12}>
                      <FormGroup>
                        <ControlLabel>Search</ControlLabel>
                        <FormControl
                          value={filters.search}
                          onChange={(e) => updateFilter("search", e.target.value)}
                          placeholder="Name or mobile"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="filter-toolbar">
                    <Button onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}>
                      {advancedFiltersOpen ? "Hide filters" : "More filters"}
                    </Button>
                    <Button bsStyle="primary" onClick={applyFilters}>
                      Apply
                    </Button>
                    <Button onClick={resetFilters}>Reset</Button>
                  </div>

                  {advancedFiltersOpen ? (
                    <Row className="simple-filter-bar metric-filter-bar">
                      <Col md={2} sm={6} xs={12}>
                        <ControlLabel>Sort by</ControlLabel>
                        <Select
                          clearable={false}
                          styles={retentionSelectStyles}
                          value={selectedValue(sortOptions, filters.sort_by)}
                          onChange={(item) => updateFilterAndLoad("sort_by", item.value)}
                          options={sortOptions}
                        />
                      </Col>
                      <Col md={2} sm={6} xs={12}>
                        <ControlLabel>Direction</ControlLabel>
                        <Select
                          clearable={false}
                          styles={retentionSelectStyles}
                          value={selectedValue(sortDirectionOptions, filters.sort_direction)}
                          onChange={(item) => updateFilterAndLoad("sort_direction", item.value)}
                          options={sortDirectionOptions}
                        />
                      </Col>
                      <Col md={1} sm={6} xs={12}>
                        <FormGroup>
                          <ControlLabel>Min orders</ControlLabel>
                          <FormControl
                            type="number"
                            min="0"
                            value={filters.min_orders}
                            onChange={(e) => updateFilter("min_orders", e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={1} sm={6} xs={12}>
                        <FormGroup>
                          <ControlLabel>Min claims</ControlLabel>
                          <FormControl
                            type="number"
                            min="0"
                            value={filters.min_claims}
                            onChange={(e) => updateFilter("min_claims", e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2} sm={6} xs={12}>
                        <FormGroup>
                          <ControlLabel>Min claim value</ControlLabel>
                          <FormControl
                            type="number"
                            min="0"
                            value={filters.min_claim_value}
                            onChange={(e) => updateFilter("min_claim_value", e.target.value)}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={2} sm={6} xs={12}>
                        <ControlLabel>Revenue</ControlLabel>
                        <Select
                          clearable={false}
                          styles={retentionSelectStyles}
                          value={selectedValue(pharmacyRevenueOptions, filters.pharmacy_revenue_status)}
                          onChange={(item) => updateFilterAndLoad("pharmacy_revenue_status", item.value)}
                          options={pharmacyRevenueOptions}
                        />
                      </Col>
                    </Row>
                  ) : null}

                  {showAdvancedSync ? (
                    <Row className="sync-panel">
                      <Col md={12} xs={12}>
                        <div className="sync-note">
                          Sync has claim flags and Gemini limits so already-read claims are not repeatedly processed.
                        </div>
                      </Col>
                      <Col md={3} sm={6} xs={12}>
                        <ControlLabel>Batch size</ControlLabel>
                        <FormControl
                          type="number"
                          min="10"
                          max="100"
                          value={syncForm.chunk_size}
                          onChange={(e) => updateSync("chunk_size", e.target.value)}
                        />
                      </Col>
                      <Col md={3} sm={6} xs={12}>
                        <ControlLabel>Max claims</ControlLabel>
                        <FormControl
                          type="number"
                          min="1"
                          max="5000"
                          value={syncForm.max_claims}
                          onChange={(e) => updateSync("max_claims", e.target.value)}
                        />
                      </Col>
                      <Col md={3} sm={6} xs={12}>
                        <ControlLabel>Gemini call limit</ControlLabel>
                        <FormControl
                          type="number"
                          min="0"
                          max="200"
                          value={syncForm.max_gemini_calls}
                          onChange={(e) => updateSync("max_gemini_calls", e.target.value)}
                        />
                      </Col>
                      <Col md={6} sm={6} xs={12}>
                        <FormGroup>
                          <ControlLabel>Specific lead IDs</ControlLabel>
                          <FormControl
                            value={syncForm.lead_ids}
                            onChange={(e) => updateSync("lead_ids", e.target.value)}
                            placeholder="123,456,789"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6} sm={6} xs={12}>
                        <FormGroup>
                          <ControlLabel>Mobile numbers</ControlLabel>
                          <FormControl
                            value={syncForm.mobile_numbers}
                            onChange={(e) => updateSync("mobile_numbers", e.target.value)}
                            placeholder="+94701234567, 0771234567"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={3} sm={6} xs={12}>
                        <ControlLabel>&nbsp;</ControlLabel>
                        <div>
                          <label>
                            <input
                              type="checkbox"
                              checked={!!syncForm.force}
                              onChange={(e) => updateSync("force", e.target.checked)}
                            />{" "}
                            Force reread
                          </label>
                        </div>
                        <Button bsStyle="info" onClick={startSync} disabled={syncLoading}>
                          {syncLoading ? "Syncing..." : "Start Sync"}
                        </Button>
                      </Col>
                      {run ? (
                        <Col md={12} xs={12}>
                          <div className="sync-status">
                            Run #{run.id}: {run.status} - {run.processed_claims || 0}/
                            {run.total_claims || 0} claims, Gemini {run.gemini_calls_used || 0}/
                            {run.max_gemini_calls || 0}
                            {run.skipped_claims ? `, skipped ${run.skipped_claims}` : ""}
                            {run.failed_claims ? `, failed ${run.failed_claims}` : ""}
                            {run.error_message ? ` (${run.error_message})` : ""}
                          </div>
                        </Col>
                      ) : null}
                    </Row>
                  ) : null}

                  <div className="table-status-bar">
                    <div className="table-result-summary">
                      <span>
                        Showing {formatNumber(pageStart)}-{formatNumber(pageEnd)} of{" "}
                        {formatNumber(pagination.total)} synced risk profiles from{" "}
                        {formatNumber(summary.claim_submitted_customers)} claim-submitted customers.
                      </span>
                      <small>
                        {formatNumber(TABLE_PAGE_SIZE)} customers per page
                      </small>
                    </div>
                    <div className="table-actions">
                      {renderPager("retention-pagination-top")}
                      <span>
                        {formatNumber(summary.unsynced_claims)} claims pending sync
                      </span>
                      <Button bsStyle="success" bsSize="small" disabled={exporting} onClick={exportExcel}>
                        {exporting ? "Downloading..." : "Download Excel"}
                      </Button>
                    </div>
                  </div>

                  {hasMultiplePages ? (
                    <div className="mobile-page-summary">
                      Page {formatNumber(pagination.current_page)} of{" "}
                      {formatNumber(pagination.last_page)} for{" "}
                      {formatNumber(pagination.total)} profiles
                    </div>
                  ) : null}

                  {loading ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="retention-table-wrap">
                      <Table responsive hover className="retention-table">
                        <thead>
                          <tr>
                            <th>Customer</th>
                            <th>Status</th>
                            <th>Disease</th>
                            <th>Why</th>
                            <th>{sortHeader("Last order", "last_order")}</th>
                            <th>{sortHeader("Orders", "orders")}</th>
                            <th>{sortHeader("Claims", "claims")}</th>
                            <th>{sortHeader("Claim value", "claim_value")}</th>
                            <th>{sortHeader("Pharmacy revenue", "pharmacy_revenue")}</th>
                            <th>Next step</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profiles.length === 0 ? (
                            <tr>
                              <td colSpan="10">
                                <center>No customers match this view</center>
                              </td>
                            </tr>
                          ) : (
                            profiles.map((profile) => (
                              <tr
                                className="retention-click-row"
                                key={profile.id}
                                onClick={() => openProfileDetails(profile)}
                              >
                                <td>
                                  <b>{profile.lead ? profile.lead.name : "-"}</b>
                                  <br />
                                  <small>{profile.lead ? profile.lead.mobile_no : ""}</small>
                                  <br />
                                  <small>
                                    {profile.lead && profile.lead.b2b
                                      ? profile.lead.b2b.company_name
                                      : ""}
                                  </small>
                                  {profile.delivery_region ? (
                                    <React.Fragment>
                                      <br />
                                      <small>{profile.delivery_region}</small>
                                    </React.Fragment>
                                  ) : null}
                                </td>
                                <td>
                                  {statusBadge(profile.business_status)}
                                  <br />
                                  <small>{profile.customer_segment}</small>
                                </td>
                                <td>{renderCondition(profile)}</td>
                                <td className="reason-cell">{profile.follow_up_reason}</td>
                                <td>{renderLastOrder(profile)}</td>
                                <td>
                                  {profile.pharmacy_order_count}
                                  <br />
                                  <small>{profile.orders_last_6_months || 0} in last 6 months</small>
                                </td>
                                <td>
                                  {profile.total_claims}
                                  <br />
                                  <small>{profile.risk_level} risk</small>
                                </td>
                                <td>
                                  Rs. {formatMoney(profile.total_claim_value)}
                                  <br />
                                  <small>
                                    Approved Rs. {formatMoney(profile.approved_claim_value)}
                                  </small>
                                </td>
                                <td>Rs. {formatMoney(profile.pharmacy_order_value)}</td>
                                <td className="next-step-cell">{profile.recommended_next_step}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  <div className="table-footer-pagination">
                    {renderPager("retention-pagination-bottom")}
                    {hasMultiplePages ? (
                      <span>
                        Showing {formatNumber(pageStart)}-{formatNumber(pageEnd)} of{" "}
                        {formatNumber(pagination.total)}
                      </span>
                    ) : null}
                  </div>

                  {renderProfileDetailModal()}

                  <Modal
                    show={campaignModalOpen}
                    onHide={() => setCampaignModalOpen(false)}
                    bsSize="large"
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>Run SMS campaign</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {campaignLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <React.Fragment>
                          <div className="campaign-builder-summary">
                            <div>
                              <span>Audience</span>
                              <b>{formatNumber(campaignPreview && campaignPreview.recipient_count)}</b>
                              <small>customers with mobile numbers</small>
                            </div>
                            <div>
                              <span>SMS length</span>
                              <b>{campaignMessage.length} chars</b>
                              <small>{smsParts(campaignMessage)} SMS part(s)</small>
                            </div>
                            <div>
                              <span>Mode</span>
                              <b>Dialog SMS</b>
                              <small>queued after confirmation</small>
                            </div>
                          </div>

                          <FormGroup>
                            <ControlLabel>Campaign name</ControlLabel>
                            <FormControl
                              value={campaignName}
                              onChange={(e) => setCampaignName(e.target.value)}
                              placeholder="Campaign name"
                            />
                          </FormGroup>

                          <FormGroup>
                            <ControlLabel>SMS body</ControlLabel>
                            <FormControl
                              componentClass="textarea"
                              rows={5}
                              value={campaignMessage}
                              onChange={(e) => setCampaignMessage(e.target.value)}
                              placeholder="Write the SMS message"
                            />
                            <p className="campaign-helper">
                              You can use {"{{name}}"}, {"{{first_name}}"}, {"{{condition}}"}, {"{{company}}"}.
                            </p>
                          </FormGroup>

                          <div className="campaign-preview-actions">
                            <Button onClick={() => previewCampaign(campaignMessage)}>
                              Update preview
                            </Button>
                          </div>

                          <div className="campaign-samples">
                            <h5>Sample preview</h5>
                            {campaignPreview &&
                            campaignPreview.sample_recipients &&
                            campaignPreview.sample_recipients.length ? (
                              campaignPreview.sample_recipients.map((recipient) => (
                                <div className="campaign-sample" key={recipient.lead_id}>
                                  <b>
                                    {recipient.name} - {recipient.mobile_no}
                                  </b>
                                  <p>{recipient.message_preview}</p>
                                </div>
                              ))
                            ) : (
                              <p className="campaign-helper">No matching recipients with mobile numbers.</p>
                            )}
                          </div>

                          {activeCampaign ? (
                            <div className="campaign-status-panel">
                              <b>Campaign #{activeCampaign.id}</b> is {activeCampaign.status}. Sent{" "}
                              {formatNumber(activeCampaign.sent_count || 0)} of{" "}
                              {formatNumber(activeCampaign.recipient_count || 0)}. Failed{" "}
                              {formatNumber(activeCampaign.failed_count || 0)}.
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button onClick={() => setCampaignModalOpen(false)}>Close</Button>
                      <Button
                        bsStyle="primary"
                        disabled={campaignLoading || campaignSending || !(campaignPreview && campaignPreview.recipient_count)}
                        onClick={sendCampaign}
                      >
                        {campaignSending ? "Queueing..." : "Send Campaign"}
                      </Button>
                    </Modal.Footer>
                  </Modal>

                  <Modal
                    show={historyModalOpen}
                    onHide={() => setHistoryModalOpen(false)}
                    bsSize="large"
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>Campaign history</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {historyLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <React.Fragment>
                          <Table responsive hover className="campaign-history-table">
                            <thead>
                              <tr>
                                <th>Campaign</th>
                                <th>Status</th>
                                <th>Audience</th>
                                <th>Sent</th>
                                <th>Failed</th>
                                <th>Orders after</th>
                                <th>Revenue after</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {campaignHistory.length === 0 ? (
                                <tr>
                                  <td colSpan="8">
                                    <center>No campaigns found</center>
                                  </td>
                                </tr>
                              ) : (
                                campaignHistory.map((campaign) => (
                                  <tr key={campaign.id}>
                                    <td>
                                      <b>{campaign.name}</b>
                                      <br />
                                      <small>{formatDate(campaign.created_at)}</small>
                                    </td>
                                    <td>{campaign.status}</td>
                                    <td>{formatNumber(campaign.recipient_count)}</td>
                                    <td>{formatNumber(campaign.sent_count)}</td>
                                    <td>{formatNumber(campaign.failed_count)}</td>
                                    <td>
                                      {formatNumber(campaign.order_count_after)}
                                      <br />
                                      <small>{formatNumber(campaign.ordering_customers_after)} customers</small>
                                    </td>
                                    <td>Rs. {formatMoney(campaign.revenue_after)}</td>
                                    <td>
                                      <Button
                                        bsSize="xsmall"
                                        onClick={() => openCampaignDetails(campaign.id)}
                                      >
                                        View
                                      </Button>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </Table>

                          {campaignDetailLoading ? <LoadingSpinner /> : null}

                          {selectedCampaign ? (
                            <div className="campaign-detail-panel">
                              <div className="campaign-detail-header">
                                <div>
                                  <h5>{selectedCampaign.name}</h5>
                                  <p>{selectedCampaign.message_body}</p>
                                </div>
                                <div>
                                  <b>Rs. {formatMoney(selectedCampaign.revenue_after)}</b>
                                  <small>{formatNumber(selectedCampaign.order_count_after)} orders after campaign</small>
                                </div>
                              </div>
                              <Table responsive condensed className="campaign-recipient-table">
                                <thead>
                                  <tr>
                                    <th>Customer</th>
                                    <th>Company</th>
                                    <th>Disease</th>
                                    <th>Status</th>
                                    <th>Sent at</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {campaignRecipients.map((recipient) => (
                                    <tr key={recipient.id}>
                                      <td>
                                        <b>{recipient.customer_name}</b>
                                        <br />
                                        <small>{recipient.mobile_no}</small>
                                      </td>
                                      <td>{recipient.company_name || "-"}</td>
                                      <td>{recipient.condition_name || "Unknown"}</td>
                                      <td>{recipient.status}</td>
                                      <td>{formatDate(recipient.sent_at)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          ) : null}
                        </React.Fragment>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button onClick={loadCampaignHistory}>Refresh</Button>
                      <Button onClick={() => setHistoryModalOpen(false)}>Close</Button>
                    </Modal.Footer>
                  </Modal>
                </React.Fragment>
              }
            />
          </Col>
        </Row>
      </Grid>
    </div>
  );
};

export default ClaimRiskRetention;
